import React, { useRef, useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import "./TreeVine.css";

/* Vertical positions for each branch as fraction of viewport height */
const BRANCH_YS = [0.30, 0.50, 0.70];

/* Branch themes — unique color per section (matches TreeVineMobile) */
const BRANCH_THEMES = [
  { color: "#F59E0B", glow: "#FDE68A", accent: "#D97706" },   // Energy ⚡
  { color: "#818CF8", glow: "#C7D2FE", accent: "#6366F1" },   // Robotics 🤖
  { color: "#67E8F9", glow: "#ECFEFF", accent: "#06B6D4" },   // Arctic ❄️
];

/* Small twig offshoots along the trunk for organic detail */
const TWIG_POSITIONS = [0.08, 0.18, 0.38, 0.44, 0.58, 0.64, 0.82, 0.92];

function TreeVine({ progress, sections }) {
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setDims({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { w, h } = dims;
  const cx = w / 2;

  /* ---- Trunk: gentle S-curves down the center ---- */
  const trunkD = useMemo(() => {
    if (!w || !h) return "";
    const step = w * 0.008;
    return [
      `M ${cx} ${h * 0.02}`,
      `C ${cx} ${h * 0.06} ${cx - step} ${h * 0.10} ${cx - step} ${h * 0.16}`,
      `C ${cx - step} ${h * 0.22} ${cx + step} ${h * 0.26} ${cx + step} ${h * 0.28}`,
      `C ${cx + step} ${h * 0.29} ${cx} ${h * 0.295} ${cx} ${h * 0.30}`,
      `C ${cx} ${h * 0.33} ${cx - step} ${h * 0.37} ${cx - step} ${h * 0.41}`,
      `C ${cx - step} ${h * 0.45} ${cx + step} ${h * 0.47} ${cx + step} ${h * 0.49}`,
      `C ${cx + step} ${h * 0.495} ${cx} ${h * 0.498} ${cx} ${h * 0.50}`,
      `C ${cx} ${h * 0.53} ${cx + step} ${h * 0.57} ${cx + step} ${h * 0.61}`,
      `C ${cx + step} ${h * 0.65} ${cx - step} ${h * 0.67} ${cx - step} ${h * 0.69}`,
      `C ${cx - step} ${h * 0.695} ${cx} ${h * 0.698} ${cx} ${h * 0.70}`,
      `C ${cx} ${h * 0.74} ${cx + step} ${h * 0.80} ${cx + step} ${h * 0.86}`,
      `C ${cx + step} ${h * 0.92} ${cx - step} ${h * 0.95} ${cx} ${h * 0.98}`,
    ].join(" ");
  }, [cx, w, h]);

  /* ---- Branch paths to each card ---- */
  const branchDs = useMemo(() => {
    if (!w || !h) return [];
    return sections.map((s, i) => {
      const y = BRANCH_YS[i] * h;
      const endX = s.side === "left" ? w * 0.18 : w * 0.82;
      const sign = s.side === "left" ? -1 : 1;
      const step = w * 0.008;
      const cp1X = cx + sign * w * 0.06;
      const cp1Y = y - h * 0.015;
      const cp2X = cx + sign * w * 0.14;
      const cp2Y = y + h * 0.008;
      const midX = cx + sign * w * 0.18;
      const midY = y - h * 0.005;
      const cp3X = cx + sign * w * 0.22;
      const cp3Y = y + h * 0.01;
      return [
        `M ${cx + sign * step} ${y}`,
        `C ${cp1X} ${cp1Y} ${cp2X} ${cp2Y} ${midX} ${midY}`,
        `C ${cp3X} ${cp3Y} ${endX} ${y - h * 0.004} ${endX} ${y}`,
      ].join(" ");
    });
  }, [cx, w, h, sections]);

  /* ---- Small twig offshoots along trunk ---- */
  const twigDs = useMemo(() => {
    if (!w || !h) return [];
    return TWIG_POSITIONS.map((t, i) => {
      const y = (0.02 + t * 0.96) * h;
      const sign = i % 2 === 0 ? -1 : 1;
      const step = w * 0.008;
      const baseX = cx + (i % 2 === 0 ? -step : step);
      const len = w * 0.022 + (i % 3) * w * 0.008;
      const endX = baseX + sign * len;
      const endY = y - h * 0.012;
      const cpX = baseX + sign * len * 0.6;
      const cpY = y - h * 0.005;
      return `M ${baseX} ${y} Q ${cpX} ${cpY} ${endX} ${endY}`;
    });
  }, [cx, w, h]);

  /* ---- Branch growth (synced with trunk reaching each Y level) ---- */
  const branchGrows = BRANCH_YS.map((by) => {
    const trunkArrives = (by - 0.02) / 0.96;
    const dur = 0.06;
    if (progress < trunkArrives) return 0;
    return Math.min((progress - trunkArrives) / dur, 1);
  });

  /* ---- Glow node opacity ---- */
  const nodeAlphas = branchGrows.map((g) =>
    g > 0.8 ? Math.min((g - 0.8) / 0.2, 1) : 0
  );

  /* ---- Growing tip position — cached path ref instead of temp DOM element ---- */
  const trunkPathRef = useRef(null);
  const tipPos = useMemo(() => {
    if (!w || !h) return null;
    const pathEl = trunkPathRef.current;
    if (!pathEl) return { x: w / 2, y: (2 + progress * 96) / 100 * h };
    try {
      const totalLen = pathEl.getTotalLength();
      const pt = pathEl.getPointAtLength(totalLen * progress);
      return { x: pt.x, y: pt.y };
    } catch {
      return { x: w / 2, y: (2 + progress * 96) / 100 * h };
    }
  }, [progress, w, h]);

  if (!w || !h) {
    return <div ref={containerRef} className="tree-vine-wrap" />;
  }

  return (
    <div ref={containerRef} className="tree-vine-wrap">
      <svg className="tree-vine-svg" width={w} height={h}>
        <defs>
          <linearGradient id="vineGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#20B2AA" />
            <stop offset="50%" stopColor="#2EC4B6" />
            <stop offset="100%" stopColor="#40E0D0" />
          </linearGradient>
          {/* Per-branch radial glow gradients */}
          <radialGradient id="branchNodeGlow0">
            <stop offset="0%" stopColor="#FDE68A" stopOpacity="1" />
            <stop offset="40%" stopColor="#F59E0B" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#D97706" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="branchNodeGlow1">
            <stop offset="0%" stopColor="#C7D2FE" stopOpacity="1" />
            <stop offset="40%" stopColor="#818CF8" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="branchNodeGlow2">
            <stop offset="0%" stopColor="#ECFEFF" stopOpacity="1" />
            <stop offset="40%" stopColor="#67E8F9" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#06B6D4" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Trunk guide — faint full path (also used for tip position calc) */}
        <path ref={trunkPathRef} d={trunkD} stroke="rgba(32,178,170,0.05)" strokeWidth="3"
          fill="none" strokeLinecap="round" />

        {/* Trunk outer glow */}
        <motion.path d={trunkD} stroke="#20B2AA" strokeWidth="12"
          fill="none" strokeLinecap="round" opacity="0.06"
          style={{ pathLength: progress }} />

        {/* Trunk mid glow */}
        <motion.path d={trunkD} stroke="#20B2AA" strokeWidth="7"
          fill="none" strokeLinecap="round" opacity="0.12"
          style={{ pathLength: progress }} />

        {/* Trunk core */}
        <motion.path d={trunkD} stroke="url(#vineGrad)" strokeWidth="3.5"
          fill="none" strokeLinecap="round"
          style={{ pathLength: progress }} />

        {/* Trunk highlight */}
        <motion.path d={trunkD} stroke="#A7FFEB" strokeWidth="1"
          fill="none" strokeLinecap="round" opacity="0.35"
          style={{ pathLength: progress }} />

        {/* Twig offshoots */}
        {twigDs.map((d, i) => {
          const twigT = 0.02 + TWIG_POSITIONS[i] * 0.96;
          const visible = progress > twigT;
          const alpha = visible ? Math.min((progress - twigT) / 0.03, 1) : 0;
          return (
            <motion.path key={`tw-${i}`} d={d}
              stroke="#40E0D0" strokeWidth="1.5" fill="none" strokeLinecap="round"
              opacity={alpha * 0.5}
              style={{ pathLength: alpha }} />
          );
        })}

        {/* Themed branches — each with its own color */}
        {branchDs.map((d, i) => {
          const grow = branchGrows[i];
          if (grow <= 0) return null;
          const theme = BRANCH_THEMES[i];
          return (
            <React.Fragment key={`branch-${i}`}>
              {/* Outer glow */}
              <motion.path d={d} stroke={theme.color} strokeWidth="10"
                fill="none" strokeLinecap="round" opacity={0.08 * grow}
                style={{ pathLength: grow }} />
              {/* Mid glow */}
              <motion.path d={d} stroke={theme.color} strokeWidth="5"
                fill="none" strokeLinecap="round" opacity={0.18 * grow}
                style={{ pathLength: grow }} />
              {/* Core */}
              <motion.path d={d} stroke={theme.color} strokeWidth="2.5"
                fill="none" strokeLinecap="round" opacity={0.6 * grow}
                style={{ pathLength: grow }} />
              {/* Highlight */}
              <motion.path d={d} stroke={theme.glow} strokeWidth="1"
                fill="none" strokeLinecap="round" opacity={0.35 * grow}
                style={{ pathLength: grow }} />
            </React.Fragment>
          );
        })}

        {/* Themed junction nodes */}
        {BRANCH_YS.map((by, i) => {
          const a = branchGrows[i];
          const step = w * 0.008;
          const sign = sections[i]?.side === "left" ? -1 : 1;
          const theme = BRANCH_THEMES[i];
          if (a <= 0) return null;
          const jx = cx + sign * step;
          const jy = by * h;
          return (
            <g key={`jn-${i}`} opacity={a}>
              <circle cx={jx} cy={jy} r="10" fill={`url(#branchNodeGlow${i})`} />
              <circle cx={jx} cy={jy} r="5" fill={theme.accent} opacity="0.2" />
              <circle cx={jx} cy={jy} r="3" fill={theme.color} opacity="0.6" />
              <circle cx={jx} cy={jy} r="1.5" fill={theme.glow} opacity="0.9" />
            </g>
          );
        })}

        {/* Decorative node dots along trunk */}
        {[0.06, 0.16, 0.34, 0.52, 0.70, 0.88, 0.94].map((t, i) => {
          const dotY = (0.02 + t * 0.96) * h;
          const visible = progress > 0.02 + t * 0.96;
          return visible ? (
            <g key={`dn-${i}`}>
              <circle cx={cx} cy={dotY} r="2.5" fill="#20B2AA" opacity="0.2" />
              <circle cx={cx} cy={dotY} r="1.2" fill="#40E0D0" opacity="0.5" />
            </g>
          ) : null;
        })}
      </svg>

      {/* Themed glow nodes at branch tips */}
      {sections.map((s, i) =>
        nodeAlphas[i] > 0 ? (
          <div key={`nd-${i}`} className={`tree-vine-node tree-vine-node--branch-${i}`}
            style={{
              left: s.side === "left" ? "18%" : "82%",
              top: `${BRANCH_YS[i] * 100}%`,
              opacity: nodeAlphas[i],
            }}
          />
        ) : null
      )}

      {/* Growing tip indicator — follows actual trunk path */}
      {tipPos && (
        <div className="tree-vine-tip"
          style={{
            left: `${tipPos.x}px`,
            top: `${tipPos.y}px`,
            opacity: progress > 0.01 && progress < 0.97 ? 1 : 0,
          }}
        />
      )}
    </div>
  );
}

export default TreeVine;
